import type { Cell, PaginationState, RowSelectionState, SortingState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useEffect, useMemo, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { SearchIcon } from "lucide-react";
import { DataTablePagination } from "./DataTabelPagination";
import type { QueryOptionsDTO } from "@/interfaces/query-options-dto";
import { SortDirection } from "@/interfaces/query-options-dto";
import useDebounce from "@/hooks/useDebounce";
import type { CustomColumnDef } from "@/types/custom-column-def";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { ColumnConfig, FiltersState } from "../data-table-filter/core/types";
import { Checkbox } from "@/components/ui/checkbox";
import { useDataTableFilters } from "../data-table-filter/hooks/use-data-table-filters";
import { DataTableFilter } from "../data-table-filter/components/data-table-filter";
import type { DataTableProps } from "./data-table-props";
import {
  DataTableCardClickableRow,
  DataTableCardLinkRow,
  DataTableCardStaticRow,
  DataTableTableClickableRow,
  DataTableTableLinkRow,
  DataTableTableStaticRow,
} from "./data-table-rows";
import { mapQueryColumnConfig } from "./map-query-column-config";

export function DataTable<TData, TValue>({
  columns,
  queryFn,
  queryKeyFn,
  onRowClick,
  rowLink,
  contentLayout = false,
  showSearch = true,
  className,
  shouldAutoFocusSearch = false,
  defaultSorting = [],
  renderFilters,
  missingRequiredFilterValues,
  missingRequiredFilterValuesMessage,
  filtersQueryFn,
  filtersQueryKeyFn,
  enableRowSelection = false,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  getRowIdFn,
}: DataTableProps<TData, TValue>) {
  const LOADING_ROWS = 3;

  if (typeof queryFn === "symbol") {
    throw new Error("queryFn is a symbol");
  }

  if (typeof queryKeyFn === "symbol") {
    throw new Error("queryKeyFn is a symbol");
  }

  const [paginationState, setPaginationState] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
  const { searchString, filters: filtersState } = useSearch({ strict: false });
  const currentSearchString = searchString ?? "";
  const [searchInputValue, setSearchInputValue] = useState(currentSearchString);
  const debouncedSearchString = useDebounce(searchInputValue, 300);
  const navigate = useNavigate();

  useEffect(() => {
    setSearchInputValue(currentSearchString);
  }, [currentSearchString]);

  useEffect(() => {
    if (debouncedSearchString === currentSearchString) {
      return;
    }

    navigate({
      to: ".",
      replace: true,
      search: (old) => ({
        ...old,
        searchString: debouncedSearchString || undefined,
      }),
    });
  }, [currentSearchString, debouncedSearchString, navigate]);

  // Use controlled or internal row selection
  const rowSelection = controlledRowSelection ?? internalRowSelection;

  const handleRowSelectionChange = (
    updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState),
  ) => {
    const newValue = typeof updaterOrValue === "function" ? updaterOrValue(rowSelection) : updaterOrValue;
    if (onRowSelectionChange) {
      onRowSelectionChange(newValue);
    } else {
      setInternalRowSelection(newValue);
    }
  };

  const queryOptions = useMemo(() => {
    const queryOptions: QueryOptionsDTO = {
      pageIndex: paginationState.pageIndex,
      pageSize: paginationState.pageSize,
      searchString: debouncedSearchString || undefined,
    };

    if (sorting && sorting.length > 0) {
      queryOptions.sortDirection = sorting[0].desc ? SortDirection.Descending : SortDirection.Ascending;

      const column = columns.find((x) => {
        if ("id" in x) {
          return x.id === sorting[0].id;
        } else if ("accessorKey" in x) {
          return x.accessorKey === sorting[0].id;
        }
      });

      if (column && "sortField" in column && column.sortField) {
        queryOptions.sortField = column.sortField;
      } else {
        queryOptions.sortField = sorting[0].id;
      }
    }

    return queryOptions;
  }, [paginationState, sorting, debouncedSearchString]);

  useEffect(() => {
    setPaginationState({ pageIndex: 0, pageSize: paginationState.pageSize });
    handleRowSelectionChange({});
  }, [filtersState, debouncedSearchString]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: queryKeyFn(queryOptions, filtersState),
    enabled: !missingRequiredFilterValues,
    queryFn: ({ signal }) => queryFn(queryOptions, signal, filtersState),
  });

  const hasStandardFilters = !!filtersQueryFn && !!filtersQueryKeyFn;

  const { data: columnsConfig, isPending: isLoadingColumnsConfig } = useQuery({
    queryKey: filtersQueryKeyFn ? filtersQueryKeyFn() : ([] as unknown[]),
    queryFn: ({ signal }) => (filtersQueryFn ? filtersQueryFn(signal) : Promise.resolve([])),
    enabled: hasStandardFilters,
  });

  const {
    columns: filterColumns,
    filters,
    actions,
    strategy,
  } = useDataTableFilters({
    strategy: "server",
    data: data?.items ?? [],
    columnsConfig:
      columnsConfig?.map(mapQueryColumnConfig) ??
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ([] as readonly ColumnConfig<TData, any, any, any>[]),
    filters: filtersState ?? [],
    onFiltersChange: (update) => {
      const newFilters =
        typeof update === "function" ? (update as (prev: FiltersState) => FiltersState)(filters ?? []) : update;

      navigate({
        to: ".",
        search: ({ filters: _, ...old }) => ({
          ...old,
          filters: newFilters.length ? newFilters : undefined,
        }),
      });
    },
  });

  // Add selection column if enabled
  const columnsWithSelection = useMemo(() => {
    if (!enableRowSelection) {
      return columns;
    }

    const selectionColumn: CustomColumnDef<TData, TValue> = {
      id: "select",
      header: ({ table }) =>
        table && (
          <Checkbox
            checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    } as CustomColumnDef<TData, TValue>;

    return [selectionColumn, ...columns];
  }, [enableRowSelection, columns]);

  // In content layout, leave unspecified widths flexible.
  const explicitlySizedColumnIds = useMemo(() => {
    return new Set(
      columnsWithSelection.flatMap((column) => {
        if (!Object.hasOwn(column, "size")) {
          return [];
        }
        if ("id" in column && column.id) {
          return [column.id];
        }
        return "accessorKey" in column && typeof column.accessorKey === "string" ? [column.accessorKey] : [];
      }),
    );
  }, [columnsWithSelection]);
  const table = useReactTable({
    data: data?.items || [],
    columns: columnsWithSelection,
    state: {
      sorting,
      pagination: paginationState,
      rowSelection,
    },
    enableRowSelection,
    getRowId: getRowIdFn,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPaginationState,
    onRowSelectionChange: handleRowSelectionChange,
    rowCount: data?.totalCount,
    pageCount: data?.totalPages,
    manualPagination: true,
    manualSorting: true,
  });

  // Helper to get column header label as text
  const getHeaderLabel = (cell: Cell<TData, unknown>) => {
    const { column } = cell;
    const { columnDef } = column;
    if (typeof columnDef.header === "string") {
      return columnDef.header;
    }
    if (typeof columnDef.header === "function") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const headerNode = columnDef.header({ column } as any);

      if (headerNode && headerNode.props && "title" in headerNode.props) {
        return headerNode.props.title;
      }
    }
    return null;
  };

  // Card rendering for mobile
  const cardRows = useMemo(() => {
    if (!data && isLoading) {
      return Array.from({ length: LOADING_ROWS }).map((_, rowIndex) => (
        <div key={rowIndex} className="rounded-md border p-4 mb-2 bg-background">
          {columns.map((_, columnIndex) => (
            <div key={columnIndex} className="mb-2">
              <Skeleton className="w-full h-4" />
            </div>
          ))}
        </div>
      ));
    } else if (table.getRowModel().rows?.length) {
      return table.getRowModel().rows.map((row) => {
        const rowLinkOptions = rowLink?.(row.original);
        const isLinkRow = !!rowLinkOptions && !rowLinkOptions.disabled;
        const isClickableRow = isLinkRow || !!onRowClick;
        const rowContent = row.getVisibleCells().map((cell) => (
          <div key={cell.id} className="mb-2">
            <div className="text-xs font-semibold text-muted-foreground">{getHeaderLabel(cell)}</div>
            <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
          </div>
        ));
        const rowClassName = cn(
          isFetching && "opacity-50",
          isClickableRow && "cursor-pointer hover:bg-muted",
          "rounded-md border p-4 mb-2 bg-background",
        );

        if (isLinkRow && rowLinkOptions) {
          return (
            <DataTableCardLinkRow key={row.id} row={row} rowLink={rowLinkOptions} className={rowClassName}>
              {rowContent}
            </DataTableCardLinkRow>
          );
        }

        if (onRowClick) {
          return (
            <DataTableCardClickableRow key={row.id} row={row} onRowClick={onRowClick} className={rowClassName}>
              {rowContent}
            </DataTableCardClickableRow>
          );
        }

        return (
          <DataTableCardStaticRow key={row.id} row={row} className={rowClassName}>
            {rowContent}
          </DataTableCardStaticRow>
        );
      });
    } else {
      return (
        <div className="rounded-md border p-8 flex items-center justify-center text-center text-sm text-foreground bg-muted/20">
          No results.
        </div>
      );
    }
  }, [table, data, isLoading, columns, isFetching, onRowClick, rowLink]);

  const tableRows = useMemo(() => {
    if (!data && isLoading) {
      return Array.from({ length: LOADING_ROWS }).map((_, rowIndex) => (
        <TableRow key={rowIndex} className={cn(!contentLayout && "table w-full table-fixed")}>
          {columns.map((_, columnIndex) => (
            <TableCell key={columnIndex}>
              <Skeleton className="w-full h-4" />
            </TableCell>
          ))}
        </TableRow>
      ));
    } else if (table.getRowModel().rows?.length) {
      return table.getRowModel().rows.map((row) => {
        const rowLinkOptions = rowLink?.(row.original);
        const isLinkRow = !!rowLinkOptions && !rowLinkOptions.disabled;
        const isClickableRow = isLinkRow || !!onRowClick;
        const rowContent = row.getVisibleCells().map((cell) => (
          <TableCell
            style={
              !contentLayout || explicitlySizedColumnIds.has(cell.column.id)
                ? { width: `${cell.column.getSize()}px` }
                : undefined
            }
            key={cell.id}
            className="truncate"
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ));
        const rowClassName = cn(
          !contentLayout && "table w-full table-fixed",
          isFetching && "opacity-50",
          isClickableRow && "cursor-pointer hover:bg-muted",
          !isClickableRow && "hover:bg-background",
        );

        if (isLinkRow && rowLinkOptions) {
          return (
            <DataTableTableLinkRow key={row.id} row={row} rowLink={rowLinkOptions} className={rowClassName}>
              {rowContent}
            </DataTableTableLinkRow>
          );
        }

        if (onRowClick) {
          return (
            <DataTableTableClickableRow key={row.id} row={row} onRowClick={onRowClick} className={rowClassName}>
              {rowContent}
            </DataTableTableClickableRow>
          );
        }

        return (
          <DataTableTableStaticRow key={row.id} row={row} className={rowClassName}>
            {rowContent}
          </DataTableTableStaticRow>
        );
      });
    } else {
      return (
        <TableRow className={cn("hover:bg-transparent", !contentLayout && "table w-full table-fixed")}>
          <TableCell colSpan={columnsWithSelection.length} className="h-24 text-center">
            No results.
          </TableCell>
        </TableRow>
      );
    }
  }, [
    table,
    data,
    isLoading,
    columns,
    columnsWithSelection.length,
    explicitlySizedColumnIds,
    contentLayout,
    isFetching,
    onRowClick,
    rowLink,
  ]);

  return (
    <div className={cn("flex-1 flex flex-col w-full gap-4 overflow-visible", className)}>
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        {renderFilters}

        {showSearch && (
          <Input
            autoFocus={shouldAutoFocusSearch}
            className="w-full sm:min-w-64 sm:flex-1 md:mr-auto md:w-72 md:flex-none"
            startAdornment={<SearchIcon size={22} className="px-1" />}
            placeholder="Search..."
            value={searchInputValue}
            onChange={(event) => setSearchInputValue(event.target.value)}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        {hasStandardFilters &&
          (isLoadingColumnsConfig ? (
            <Skeleton className="w-36 h-8" />
          ) : (
            <DataTableFilter filters={filters} columns={filterColumns} actions={actions} strategy={strategy} />
          ))}
      </div>

      {missingRequiredFilterValues ? (
        <div className="rounded-md border flex-1 flex flex-col overflow-hidden p-8 items-center justify-center">
          {missingRequiredFilterValuesMessage || "Please select a filter value to see results."}
        </div>
      ) : (
        <>
          <div className="rounded-md md:border md:flex-1 flex flex-col overflow-hidden">
            <div className="block md:hidden w-full">{cardRows}</div>
            <Table
              className={
                contentLayout
                  ? "hidden h-auto w-full table-fixed md:table"
                  : "w-full relative overflow-hidden flex-1 h-full hidden flex-col md:table"
              }
            >
              {contentLayout ? (
                <colgroup>
                  {table.getVisibleLeafColumns().map((column) => (
                    <col
                      key={column.id}
                      style={explicitlySizedColumnIds.has(column.id) ? { width: `${column.getSize()}px` } : undefined}
                    />
                  ))}
                </colgroup>
              ) : null}
              <TableHeader className={cn(!contentLayout && "table w-full table-fixed")}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className={cn(
                      "sticky top-14 bg-background hover:bg-background",
                      !contentLayout && "table w-full table-fixed",
                    )}
                  >
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead
                          style={
                            !contentLayout || explicitlySizedColumnIds.has(header.column.id)
                              ? { width: `${header.getSize()}px` }
                              : undefined
                          }
                          key={header.id}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody className={cn(!contentLayout && "table-fixed w-full block overflow-auto")}>
                {tableRows}
              </TableBody>
            </Table>
          </div>

          <DataTablePagination table={table} isLoading={isLoading} />
        </>
      )}
    </div>
  );
}
